import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isMotionReduced } from "@/shared/lib/hooks/useReducedMotion";
import { useChatRun } from "../hooks/ChatRunProvider";
import { useForkConversation } from "../hooks/useBranching";
import { useModels } from "../hooks/useModels";
import { modelLabel } from "../lib/models";
import { messageAttachmentViews } from "../lib/attachments";
import type { ThreadMessage } from "../lib/thread";
import { historicalToolStates } from "../lib/toolCalls";
import { AssistantMessage } from "./AssistantMessage";
import { BranchSwitcher } from "./BranchSwitcher";
import { RegenerateIcon } from "./icons";
import { MessageActions } from "./MessageActions";
import { UserMessage } from "./UserMessage";

export interface ThreadProps {
  items: ThreadMessage[];
  conversationId: string | null;
  /** Current composer web-search toggle, inherited by rerun (regenerate). */
  webSearch: boolean;
  /** Bumped on each composer send; lifts the new user turn to the top. */
  sendNonce: number;
}

/** Renders the merged thread (server + live) and keeps it pinned to the bottom while streaming. */
export function Thread({ items, conversationId, webSearch, sendNonce }: ThreadProps) {
  const { t } = useTranslation();
  const { run, send, regenerate, confirmTool, selectedModelId } = useChatRun();
  const fork = useForkConversation();

  // Re-run a failed live turn. If the user message already persisted, regenerate
  // its reply (no duplicate user turn); otherwise re-send the text as a fresh turn.
  function retryLive() {
    if (!run) return;
    const parent = run.mode === "regenerate" ? run.parentMessageId : run.userMessageId;
    if (run.conversationId && parent) {
      regenerate({ conversationId: run.conversationId, parentMessageId: parent, webSearch });
    } else {
      send({ conversationId: run.conversationId, text: run.userText, webSearch });
    }
  }
  const { data: modelsData } = useModels();

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const model of modelsData?.items ?? []) map.set(model.id, modelLabel(model));
    return map;
  }, [modelsData]);

  const originById = useMemo(() => {
    const map = new Map<string, string>();
    for (const model of modelsData?.items ?? []) map.set(model.id, model.origin);
    return map;
  }, [modelsData]);

  const liveModelName = selectedModelId ? nameById.get(selectedModelId) : undefined;
  const liveModelOrigin = selectedModelId ? originById.get(selectedModelId) : undefined;
  const streamingLabel = run?.phase === "searching" ? t("chat.searchingWeb") : t("chat.thinking");

  // Stick to the bottom while content grows, unless the user has scrolled up.
  const contentRef = useRef<HTMLDivElement>(null);
  const liveUserRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const pinnedRef = useRef(true);
  const signature = useMemo(() => {
    const last = items[items.length - 1];
    const liveLen = last?.kind === "liveAssistant" ? last.content.length : 0;
    // Tool-card status flips (proposed → executing → done, chained calls)
    // change the layout without changing content length — include them so
    // the bottom pin tracks the cards.
    const toolSig = run?.tools.map((tool) => tool.status).join(",") ?? "";
    return `${items.length}:${liveLen}:${run?.status ?? ""}:${toolSig}`;
  }, [items, run?.status, run?.tools]);

  // Resolve (and cache) the scrolling ancestor.
  const getScroller = useCallback(() => {
    if (scrollerRef.current) return scrollerRef.current;
    let el: HTMLElement | null = contentRef.current;
    while (el) {
      const overflowY = getComputedStyle(el).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") break;
      el = el.parentElement;
    }
    scrollerRef.current = el;
    return el;
  }, []);

  // Scroll to the true maximum — past the thread's trailing padding — so the last
  // line clears the docked composer instead of stopping under it.
  const pinToBottom = useCallback(() => {
    const el = getScroller();
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "instant" });
  }, [getScroller]);

  useEffect(() => {
    const el = getScroller();
    if (!el) return;
    const onScroll = () => {
      pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [getScroller]);

  // Pin before paint and without animation, so a reopened conversation simply
  // appears at the bottom (no visible scroll) and streaming stays glued.
  useLayoutEffect(() => {
    if (pinnedRef.current) pinToBottom();
  }, [signature, pinToBottom]);

  // Late layout shifts (web-font swap on reload, async media) move the true
  // bottom after the initial pin — re-pin while the user is still at the bottom.
  useEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) pinToBottom();
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [pinToBottom]);

  // On send, lift the new user turn to the top so the reply has room to stream
  // below it, and release the bottom pin so streaming doesn't snap it back down.
  useLayoutEffect(() => {
    if (sendNonce === 0) return;
    pinnedRef.current = false;
    // Call-time read — the explicit `behavior` option wins over the CSS
    // `scroll-behavior` zeroing, so reduce-motion must be honored here in JS.
    liveUserRef.current?.scrollIntoView({
      block: "start",
      behavior: isMotionReduced() ? "instant" : "smooth",
    });
  }, [sendNonce]);

  return (
    <div
      ref={contentRef}
      data-testid="message-list"
      className="mx-auto w-full max-w-[760px] flex-1 px-6 pb-18 pt-10 max-[880px]:px-4"
    >
      {items.map((item) => {
        if (item.kind === "liveUser") {
          return (
            <div key={item.key} ref={liveUserRef} className="scroll-mt-6">
              <UserMessage content={item.content} attachments={item.attachments} />
            </div>
          );
        }
        if (item.kind === "liveAssistant") {
          const status = item.status;
          return (
            <AssistantMessage
              key={item.key}
              content={item.content}
              messageId="live-assistant"
              modelName={liveModelName}
              modelOrigin={liveModelOrigin}
              sources={item.sources}
              tools={item.tools}
              streaming={status === "streaming" || status === "awaiting_tool" || status === "tool_running"}
              streamingPhase={run?.phase ?? null}
              streamingLabel={streamingLabel}
              errored={status === "error"}
              error={run?.error ?? null}
              onToolDecision={confirmTool}
              footer={
                status === "error" ? (
                  <button
                    type="button"
                    onClick={retryLive}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-pill bg-bubble px-3 py-1.5 font-sans text-[13px] font-medium text-ink outline-none transition-colors duration-150 ease-house hover:bg-tint focus-visible:bg-tint [&_svg]:size-3.5"
                  >
                    <RegenerateIcon />
                    {t("messages.retryResponse")}
                  </button>
                ) : undefined
              }
            />
          );
        }

        const message = item.message;
        if (message.role === "user") {
          return (
            <UserMessage
              key={item.key}
              content={message.content}
              attachments={messageAttachmentViews(message.attachments)}
            />
          );
        }

        const parentMessageId = message.parent_message_id ?? null;
        const canRegenerate = conversationId !== null && parentMessageId !== null;
        return (
          <AssistantMessage
            key={item.key}
            content={message.content}
            messageId={message.id}
            modelName={message.model_id ? nameById.get(message.model_id) : undefined}
            modelOrigin={message.model_id ? originById.get(message.model_id) : undefined}
            sources={message.sources}
            tools={historicalToolStates(message)}
            footer={
              <div className="mt-3 flex items-center gap-3">
                <MessageActions
                  text={message.content}
                  onRegenerate={
                    canRegenerate
                      ? () => regenerate({ conversationId, parentMessageId, webSearch })
                      : undefined
                  }
                  onFork={
                    conversationId !== null
                      ? () => fork.mutate({ conversationId, messageId: message.id })
                      : undefined
                  }
                />
                {conversationId !== null ? (
                  <BranchSwitcher message={message} conversationId={conversationId} />
                ) : null}
              </div>
            }
          />
        );
      })}
    </div>
  );
}
